import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    FormControlLabel,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    Popover,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { alpha } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay, type PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/en-au';
import { SectionLabel } from '../primitives';
import type { CalendarEvent, StaffSummary } from '../../hooks/useCalendar';
import { formatShort, isWeekend, rangeOf, type IsoDate } from '../../lib/dates';
import { apiFetch } from '../../lib/api';

/** Someone booked on one of the picked days — on this job or elsewhere. */
interface OtherBooking {
    readonly id: number;
    readonly staffId: number;
    readonly date: IsoDate;
    readonly eventId: number;
    readonly jobNumber: string | null;
    readonly siteAddress: string | null;
    /** Set for leave and other non-job time. */
    readonly categoryName: string | null;
    /** On a timesheet already; adding here leaves it where it is. */
    readonly timesheetLinked: boolean;
}

/**
 * The categories that mean "not at work today". Someone on one of these on any
 * picked day gets no tick box at all, so nobody can be crewed onto a day they
 * are away.
 *
 * Not every non-job category belongs here: Public Holiday and Trade School are
 * also `is_leave` in the database (it means "not chargeable", per the column
 * comment, not "absent"), and people are sometimes crewed over them, so those
 * two keep the amber warning and stay tickable.
 *
 * Matched on the name, which is unique in `calendar_category` and is what
 * /calendar/bookings sends; the ids never reach the client. Adding a category
 * in the dashboard therefore means adding it here too.
 */
const ABSENT_CATEGORIES: ReadonlySet<string> = new Set([
    'Annual Leave',
    'Sick Leave',
    'Unpaid Leave',
    'Compassionate Leave',
]);

/** One other job or leave block a person is on, and on which of the picked days. */
interface ElsewhereLine {
    readonly eventId: number;
    readonly label: string;
    readonly isLeave: boolean;
    /** Leave that means away — see ABSENT_CATEGORIES. */
    readonly isAbsence: boolean;
    readonly dates: readonly IsoDate[];
    /** Any of those days is on a timesheet, so it stays put. */
    readonly timesheetLinked: boolean;
}

/** "Thu 24 Sep", "Mon 21 Sep, Tue 22 Sep", or "5 days · Mon 21 Sep → Fri 25 Sep". */
function describeDates(dates: readonly IsoDate[]): string {
    const sorted = [...dates].sort();
    if (sorted.length <= 2) {
        return sorted.map(formatShort).join(', ');
    }
    return `${String(sorted.length)} days · ${formatShort(sorted[0] ?? '')} → ${formatShort(sorted[sorted.length - 1] ?? '')}`;
}

/**
 * Pick the crew for the selected job-days.
 *
 * The job and the days are already chosen — they are the cells that were
 * dragged — so this asks the one remaining question: who. That is the same
 * question the old "Select Staff" dropdown asked, except it is asked once for
 * the whole block instead of once per person per day.
 *
 * Anyone already booked elsewhere on one of these days is flagged but not
 * blocked: working two jobs in a day is normal here (it happens on 14% of
 * staff-days in the production data), so this warns and lets the scheduler
 * decide.
 */

/** The days picked so far, read by each calendar day to draw itself. */
const SelectedDaysContext = createContext<ReadonlySet<IsoDate>>(new Set());

/**
 * A calendar day that shows whether it is picked.
 *
 * MUI's own `selected` marks a single value; this draws the whole set, so the
 * calendar reads as "these are the days" rather than "this is the date".
 */
function SelectableDay(props: PickersDayProps): React.JSX.Element {
    const days = useContext(SelectedDaysContext);
    const { day, outsideCurrentMonth, ...rest } = props;
    const picked = !outsideCurrentMonth && days.has(day.format('YYYY-MM-DD'));
    return (
        <PickersDay
            {...rest}
            day={day}
            outsideCurrentMonth={outsideCurrentMonth}
            selected={picked}
            sx={(theme) => ({
                fontWeight: picked ? 700 : 400,
                ...(day.day() === 0 || day.day() === 6 ? { color: 'text.disabled' } : {}),
                // MUI shades the keyboard-focused day and deepens the selected
                // one on focus; with many days picked that read as three
                // different states. Picked is one look, unpicked is plain.
                ...(picked
                    ? {
                          '&, &.Mui-selected, &:hover, &:focus, &.Mui-selected:hover, &.Mui-selected:focus': {
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                          },
                      }
                    : {
                          '&:focus, &.Mui-focusVisible': { bgcolor: 'transparent' },
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                      }),
            })}
        />
    );
}

export interface PickerTarget {
    readonly event: CalendarEvent;
    readonly dates: readonly IsoDate[];
    readonly cellCount: number;
}

interface StaffPickerProps {
    readonly open: boolean;
    readonly target: PickerTarget | null;
    readonly staff: readonly StaffSummary[];
    readonly saving: boolean;
    readonly error: string | null;
    readonly onClose: () => void;
    /**
     * `staffIds` to add on `dates`; `removeIds` are this job's assignment rows
     * for the people who were unticked.
     */
    readonly onSubmit: (
        staffIds: readonly number[],
        shift: { start: string | null; end: string | null },
        dates: readonly IsoDate[],
        removeIds: readonly number[],
    ) => void;
}

/**
 * The shifts that exist in production. 07:00 and 15:30 are 91% of all start
 * times in the legacy data and only six distinct values exist at all, so these
 * are buttons rather than a time picker.
 */
const SHIFTS = [
    { id: 'day', label: 'Day 07:00–15:30', start: '07:00', end: '15:30' },
    { id: 'early', label: 'Early 06:30–15:00', start: '06:30', end: '15:00' },
    { id: 'afternoon', label: 'Afternoon 15:30', start: '15:30', end: null },
    { id: 'none', label: 'No set time', start: null, end: null },
] as const;

type ShiftId = (typeof SHIFTS)[number]['id'];

/** Legacy text columns hold whitespace where they mean NULL. See JobGrid. */
function blankToNull(value: string | null): string | null {
    if (value === null) {
        return null;
    }
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
}

function eventTitle(event: CalendarEvent): string {
    return blankToNull(event.siteAddress) ?? blankToNull(event.jobNumber) ?? 'Untitled';
}

export function StaffPicker({
    open,
    target,
    staff,
    saving,
    error,
    onClose,
    onSubmit,
}: StaffPickerProps): React.JSX.Element {
    /**
     * Ticks the user changed. A person's tick is their override if they have
     * one, otherwise whether they are already on this job for the picked days —
     * so the crew opens ticked, and stays right as days are added or removed.
     */
    const [overrides, setOverrides] = useState<ReadonlyMap<number, boolean>>(new Map());
    const [query, setQuery] = useState('');
    const [shift, setShift] = useState<ShiftId>('day');

    /**
     * The days this add writes to. Starts as the cells that were selected (one,
     * from a cell's "+ Staff"), and more are picked on the popup calendar —
     * including days past the job's end, which the API widens the job to.
     */
    const [days, setDays] = useState<ReadonlySet<IsoDate>>(new Set());
    const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);
    /**
     * Range picking is the default: click the first day, then the last. A job is
     * crewed in runs of days, so that is two clicks where toggling is five.
     * `rangeStart` is the first click, waiting for the second.
     */
    const [pickMode, setPickMode] = useState<'range' | 'days'>('range');
    const [rangeStart, setRangeStart] = useState<IsoDate | null>(null);
    const [includeWeekends, setIncludeWeekends] = useState(false);
    useEffect(() => {
        if (open) {
            const initial = target?.dates ?? [];
            setDays(new Set(initial));
            // A save closes the panel from the page, not through close(), so
            // the last session's ticks are cleared here as well.
            setOverrides(new Map());
            // Opened from one cell's "+ Staff": that day is the first click, so
            // a single click on the last day fills the run.
            setRangeStart(initial.length === 1 ? (initial[0] ?? null) : null);
        }
    }, [open, target]);

    function pickDay(d: IsoDate): void {
        if (pickMode === 'days') {
            toggleDay(d);
            return;
        }
        if (rangeStart !== null && d !== rangeStart) {
            const [from, to] = d < rangeStart ? [d, rangeStart] : [rangeStart, d];
            const run = rangeOf(from, to).filter((x) => includeWeekends || !isWeekend(x) || x === d || x === rangeStart);
            setDays((prev) => new Set([...prev, ...run]));
            setRangeStart(null);
            return;
        }
        if (rangeStart === d) {
            toggleDay(d);
            setRangeStart(null);
            return;
        }
        if (days.has(d)) {
            toggleDay(d);
            return;
        }
        toggleDay(d);
        setRangeStart(d);
    }
    const sortedDays = useMemo(() => [...days].sort(), [days]);

    /**
     * Where each person already is on the picked days, from the server — the
     * days are often outside the range the board loaded, so its own clash map
     * cannot answer this.
     */
    const [bookings, setBookings] = useState<readonly OtherBooking[]>([]);
    const bookingsKey = sortedDays.join(',');
    const eventId = target?.event.id ?? null;
    useEffect(() => {
        if (!open || eventId === null || bookingsKey === '') {
            setBookings([]);
            return;
        }
        let cancelled = false;
        apiFetch<{ bookings: readonly OtherBooking[] }>('/calendar/bookings', {
            method: 'POST',
            body: JSON.stringify({ dates: bookingsKey.split(',') }),
        })
            .then((r) => {
                if (!cancelled) {
                    setBookings(r.bookings);
                }
            })
            .catch(() => {
                // A warning, not a gate: failing to load it must not block adding.
                if (!cancelled) {
                    setBookings([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open, eventId, bookingsKey]);

    /** This job's own crew on the picked days: their row ids and which days. */
    const onThisJob = useMemo(() => {
        const m = new Map<number, { ids: number[]; dates: Set<IsoDate> }>();
        for (const b of bookings) {
            if (b.eventId !== eventId) {
                continue;
            }
            const entry = m.get(b.staffId) ?? { ids: [], dates: new Set<IsoDate>() };
            entry.ids.push(b.id);
            entry.dates.add(b.date);
            m.set(b.staffId, entry);
        }
        return m;
    }, [bookings, eventId]);

    const elsewhere = useMemo(() => {
        const byStaff = new Map<number, Map<number, ElsewhereLine>>();
        for (const b of bookings) {
            if (b.eventId === eventId) {
                continue;
            }
            const lines = byStaff.get(b.staffId) ?? new Map<number, ElsewhereLine>();
            const prev = lines.get(b.eventId);
            const label =
                b.categoryName ??
                b.siteAddress ??
                (b.jobNumber === null ? 'Another job' : `Job ${b.jobNumber}`);
            lines.set(b.eventId, {
                eventId: b.eventId,
                label,
                isLeave: b.categoryName !== null,
                isAbsence: b.categoryName !== null && ABSENT_CATEGORIES.has(b.categoryName),
                dates: [...(prev?.dates ?? []), b.date],
                timesheetLinked: (prev?.timesheetLinked ?? false) || b.timesheetLinked,
            });
            byStaff.set(b.staffId, lines);
        }
        const out = new Map<number, readonly ElsewhereLine[]>();
        for (const [staffId, lines] of byStaff) {
            out.set(staffId, [...lines.values()]);
        }
        return out;
    }, [bookings, eventId]);

    /**
     * Who is away on at least one of the picked days. One leave day is enough:
     * a range that runs over someone's leave is narrowed or split rather than
     * half-filled, which keeps the panel's promise that a tick means "on this
     * job for these days".
     *
     * Someone already on this job stays tickable even while away, so a booking
     * made before the leave was entered can still be taken off here.
     */
    const awayStaff = useMemo(() => {
        const out = new Set<number>();
        for (const [staffId, lines] of elsewhere) {
            if (!onThisJob.has(staffId) && lines.some((l) => l.isAbsence)) {
                out.add(staffId);
            }
        }
        return out;
    }, [elsewhere, onThisJob]);

    function toggleDay(d: IsoDate): void {
        setDays((prev) => {
            const next = new Set(prev);
            if (next.has(d)) {
                next.delete(d);
            } else {
                next.add(d);
            }
            return next;
        });
    }

    const filtered = useMemo(() => {
        // "Do not show as staff on calendar" hides a person here — unless they
        // are already on this job, so they can still be seen and unticked.
        const pickable = staff.filter((s) => s.showOnCalendar !== false || onThisJob.has(s.staffId));
        const q = query.trim().toLowerCase();
        if (q === '') {
            return pickable;
        }
        return pickable.filter(
            (s) => s.name.toLowerCase().includes(q) || (s.titleName ?? '').toLowerCase().includes(q),
        );
    }, [staff, query, onThisJob]);


    function isChecked(staffId: number): boolean {
        // Away wins over a tick made before the leave days were added to the
        // selection, so `toAdd` below can never carry someone who is off.
        if (awayStaff.has(staffId)) {
            return false;
        }
        return overrides.get(staffId) ?? onThisJob.has(staffId);
    }

    function toggle(staffId: number): void {
        if (awayStaff.has(staffId)) {
            return;
        }
        setOverrides((prev) => new Map(prev).set(staffId, !isChecked(staffId)));
    }

    // Only people ticked and missing at least one picked day are sent: the
    // crew already here on every day has nothing to add, and sending them
    // would still move them off anything else they are on.
    const toAdd = staff
        .map((s) => s.staffId)
        .filter((id) => isChecked(id) && (onThisJob.get(id)?.dates.size ?? 0) < days.size);
    const addCount = toAdd.reduce((n, id) => n + days.size - (onThisJob.get(id)?.dates.size ?? 0), 0);
    const toRemove = [...onThisJob.entries()].filter(([id]) => overrides.get(id) === false);
    const removeIds = toRemove.flatMap(([, entry]) => entry.ids);

    const chosenShift = SHIFTS.find((s) => s.id === shift) ?? SHIFTS[0];
    const canSubmit =
        !saving && target !== null && days.size > 0 && (toAdd.length > 0 || removeIds.length > 0);

    function submit(): void {
        if (!canSubmit) {
            return;
        }
        onSubmit(toAdd, { start: chosenShift.start, end: chosenShift.end }, sortedDays, removeIds);
    }

    function close(): void {
        setOverrides(new Map());
        setCalendarAnchor(null);
        setQuery('');
        onClose();
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={close}
            slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 }, p: 2.5, display: 'flex' } } }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                    {/* Editing once there is a crew to change, adding before. */}
                    {onThisJob.size > 0 ? 'Edit staff' : 'Add staff'}
                </Typography>
                <IconButton onClick={close} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* The job and days are read back so a mis-drag is obvious before it
                writes anything. */}
            {target !== null ? (
                <Box sx={{ mb: 2 }}>
                    <SectionLabel divider={false}>Job</SectionLabel>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'success.light' }}>
                        {eventTitle(target.event)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                        {[
                            blankToNull(target.event.jobNumber) === null
                                ? null
                                : `Job ${String(blankToNull(target.event.jobNumber))}`,
                            blankToNull(target.event.clientName),
                        ]
                            .filter((v): v is string => v !== null)
                            .join(' · ')}
                    </Typography>
                    {/* The days, as a field that opens a calendar. Clicking it is
                        how days are added — including ones past the job's end. */}
                    <TextField
                        size="small"
                        fullWidth
                        label="Days"
                        value={
                            sortedDays.length === 0
                                ? ''
                                : sortedDays.length === 1
                                  ? formatShort(sortedDays[0] ?? '')
                                  : `${String(sortedDays.length)} days · ${formatShort(sortedDays[0] ?? '')} → ${formatShort(sortedDays[sortedDays.length - 1] ?? '')}`
                        }
                        placeholder="Pick at least one day"
                        onClick={(e) => {
                            setCalendarAnchor(e.currentTarget);
                        }}
                        error={sortedDays.length === 0}
                        slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                                readOnly: true,
                                sx: { cursor: 'pointer', '& input': { cursor: 'pointer' } },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" edge="end" aria-label="Pick days">
                                            <CalendarMonthIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{ mb: 1 }}
                    />
                    {sortedDays.length > 1 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {sortedDays.map((d) => (
                                <Chip
                                    key={d}
                                    size="small"
                                    label={formatShort(d)}
                                    onDelete={() => {
                                        toggleDay(d);
                                    }}
                                />
                            ))}
                        </Box>
                    ) : null}
                    <Popover
                        open={calendarAnchor !== null}
                        anchorEl={calendarAnchor}
                        onClose={() => {
                            setCalendarAnchor(null);
                        }}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        slotProps={{ paper: { sx: { mt: 0.5 } } }}
                    >
                        <Box sx={{ px: 2, pt: 1.5 }}>
                            <ToggleButtonGroup
                                exclusive
                                fullWidth
                                size="small"
                                value={pickMode}
                                onChange={(_e, v: 'range' | 'days' | null) => {
                                    if (v !== null) {
                                        setPickMode(v);
                                        setRangeStart(null);
                                    }
                                }}
                            >
                                <ToggleButton value="range">Range</ToggleButton>
                                <ToggleButton value="days">Single days</ToggleButton>
                            </ToggleButtonGroup>
                            {pickMode === 'range' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                        {rangeStart === null
                                            ? 'Click the first day'
                                            : `From ${formatShort(rangeStart)} · click the last day`}
                                    </Typography>
                                    <FormControlLabel
                                        sx={{ mr: 0 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={includeWeekends}
                                                onChange={(e) => {
                                                    setIncludeWeekends(e.target.checked);
                                                }}
                                            />
                                        }
                                        label={<Typography sx={{ fontSize: '0.75rem' }}>Include weekends</Typography>}
                                    />
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', py: 1.1 }}>
                                    Click days to add or remove them
                                </Typography>
                            )}
                        </Box>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-au">
                            <SelectedDaysContext.Provider value={days}>
                                <DateCalendar
                                    // Uncontrolled selection: the value stays null and each
                                    // pick toggles a day in `days`, so any set of days —
                                    // not just a range — can be chosen, across months.
                                    value={null}
                                    referenceDate={dayjs(sortedDays[0] ?? target.dates[0])}
                                    onChange={(v: Dayjs | null) => {
                                        if (v !== null) {
                                            pickDay(v.format('YYYY-MM-DD'));
                                        }
                                    }}
                                    slots={{ day: SelectableDay }}
                                    showDaysOutsideCurrentMonth
                                />
                            </SelectedDaysContext.Provider>
                        </LocalizationProvider>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                pb: 1.5,
                            }}
                        >
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                {`${String(days.size)} ${days.size === 1 ? 'day' : 'days'} picked`}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    disabled={days.size === 0}
                                    onClick={() => {
                                        setDays(new Set());
                                        setRangeStart(null);
                                    }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => {
                                        setCalendarAnchor(null);
                                    }}
                                >
                                    Done
                                </Button>
                            </Box>
                        </Box>
                    </Popover>
                </Box>
            ) : null}

            <Divider sx={{ mb: 1.5 }} />

            <TextField
                size="small"
                placeholder="Search staff or skillset"
                value={query}
                onChange={(e) => {
                    setQuery(e.target.value);
                }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{ mb: 1 }}
            />

            <List dense sx={{ flex: 1, overflow: 'auto', minHeight: 140 }}>
                {filtered.map((s) => {
                    const lines = elsewhere.get(s.staffId) ?? [];
                    const here = onThisJob.get(s.staffId);
                    const away = awayStaff.has(s.staffId);
                    return (
                        <ListItemButton
                            key={s.staffId}
                            onClick={() => {
                                toggle(s.staffId);
                            }}
                            // Top-aligned, so the checkbox and dot stay on the name's
                            // line however tall the note below makes the row; the
                            // hairline keeps one person from running into the next.
                            divider
                            disableRipple={away}
                            sx={{
                                alignItems: 'flex-start',
                                py: 0.75,
                                px: 1,
                                gap: 1,
                                // Nothing to click on someone who is away, so the row
                                // does not offer itself as clickable either.
                                ...(away ? { cursor: 'default', '&:hover': { bgcolor: 'transparent' } } : {}),
                            }}
                        >
                            {/* Away: no tick box, so they cannot be put on the job at
                                all. The icon holds the column the box would have, and
                                the amber note below says which leave and which days. */}
                            {away ? (
                                <EventBusyIcon sx={{ fontSize: 20, mt: '1px', color: 'warning.main', flex: '0 0 auto' }} />
                            ) : (
                                <Checkbox
                                    size="small"
                                    checked={isChecked(s.staffId)}
                                    tabIndex={-1}
                                    disableRipple
                                    sx={{ p: 0, mt: '1px' }}
                                />
                            )}
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: s.colour ?? 'text.disabled',
                                    flex: '0 0 auto',
                                    mt: '6px',
                                }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography noWrap sx={{ fontSize: '0.84rem', fontWeight: 600 }}>
                                    {s.name}
                                </Typography>
                                {s.titleName !== null ? (
                                    <Typography noWrap sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                                        {s.titleName}
                                    </Typography>
                                ) : null}
                                {here === undefined ? null : (
                                    <Chip
                                        size="small"
                                        label={
                                            !isChecked(s.staffId)
                                                ? 'Will be taken off this job'
                                                : here.dates.size < days.size
                                                  ? `On this job · ${String(here.dates.size)} of ${String(days.size)} days`
                                                  : 'On this job'
                                        }
                                        color={isChecked(s.staffId) ? 'success' : 'error'}
                                        variant="outlined"
                                        sx={{ mt: 0.5, height: 20, fontSize: '0.66rem', fontWeight: 700 }}
                                    />
                                )}
                                {lines.length > 0 ? (
                                    <Box
                                        sx={(theme) => ({
                                            mt: 0.75,
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            borderLeft: '3px solid',
                                            borderColor: 'warning.main',
                                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                        })}
                                    >
                                        {/* Jobs and leave read the same way: where they
                                            are, and on which of the picked days. */}
                                        {lines.map((l) => {
                                            const Icon = l.isLeave ? EventBusyIcon : WarningAmberIcon;
                                            return (
                                                <Box key={l.eventId}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            color: 'warning.main',
                                                        }}
                                                    >
                                                        <Icon sx={{ fontSize: 14 }} />
                                                        <Typography
                                                            sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'inherit' }}
                                                        >
                                                            {l.isLeave ? `On ${l.label}` : 'Already on job'}
                                                        </Typography>
                                                    </Box>
                                                    {l.isLeave ? null : (
                                                        <Typography
                                                            noWrap
                                                            sx={{ fontSize: '0.72rem', color: 'text.primary', lineHeight: 1.5 }}
                                                        >
                                                            {l.label}
                                                        </Typography>
                                                    )}
                                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                                        {describeDates(l.dates)}
                                                    </Typography>
                                                    {/* Said under each job, so it is clear which one
                                                        it is about. Leave and other time show just
                                                        what and when. */}
                                                    {l.isLeave ? null : (
                                                        <Typography
                                                            sx={{ fontSize: '0.68rem', fontStyle: 'italic', color: 'text.secondary' }}
                                                        >
                                                            {l.timesheetLinked
                                                                ? 'Already on the timesheet — stays, not moved'
                                                                : 'Employee will be moved to this job'}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ) : null}
                            </Box>
                        </ListItemButton>
                    );
                })}
                {filtered.length === 0 ? (
                    <Typography sx={{ px: 2, py: 1, fontSize: '0.8rem', color: 'text.disabled' }}>
                        No staff match "{query}"
                    </Typography>
                ) : null}
            </List>

            <Divider sx={{ my: 1.5 }} />

            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.75 }}>Shift</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
                {SHIFTS.map((s) => (
                    <Button
                        key={s.id}
                        size="small"
                        variant={shift === s.id ? 'contained' : 'outlined'}
                        onClick={() => {
                            setShift(s.id);
                        }}
                        sx={{ fontSize: '0.7rem' }}
                    >
                        {s.label}
                    </Button>
                ))}
            </Stack>

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                    {error}
                </Alert>
            ) : null}

            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth variant="outlined" onClick={close} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={submit}
                    disabled={!canSubmit}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {addCount > 0 && removeIds.length > 0
                        ? `Save · add ${String(addCount)}, remove ${String(removeIds.length)}`
                        : removeIds.length > 0
                          ? `Remove ${String(removeIds.length)}`
                          : `Add${addCount > 0 ? ` ${String(addCount)}` : ''}`}
                </Button>
            </Box>

            <Typography sx={{ mt: 1.25, fontSize: '0.7rem', color: 'text.disabled' }}>
                Ticked = on this job for these days. Untick someone to take them off.
                {awayStaff.size > 0 ? ' Anyone on leave for one of these days has no tick box.' : ''}
            </Typography>
        </Drawer>
    );
}
